/**
 * Политика TK-метки: как она выглядит и что считается валидным литералом.
 *
 * Модуль намеренно чистый — ни ProseMirror, ни React, ни DOM. Его потребляют
 * и редактор (узел, правило ввода, инвариант целостности), и статистика,
 * которая до ProseMirror вообще не доходит и считает по HTML-строке.
 */

export const TK_MARKER_ATTR = 'data-tk-marker';

/**
 * Селектор включает тег намеренно.
 *
 * Схема редактора матчит именно `span[data-tk-marker]`. Если статистика будет
 * матчить любой тег, `<div data-tk-marker>` исчезнет из проектного счётчика, но
 * останется в живом — два пути разойдутся ровно там, где обязаны совпадать.
 */
export const TK_MARKER_SELECTOR = `span[${TK_MARKER_ATTR}]`;
export const TK_KIND_ATTR = 'data-kind';
export const TK_ID_ATTR = 'data-tk-id';
export const TK_DEFAULT_KIND = 'TK';

export interface ITkLiteral {
  kind: string;
  payload: string;
}

export interface ITkLiteralMatch extends ITkLiteral {
  /** Индекс открывающей скобки в исходной строке. */
  from: number;
  /** Индекс сразу за закрывающей скобкой. */
  to: number;
}

/**
 * Буквы «TK» принимаются и латиницей, и кириллицей: в русской раскладке
 * печатается именно «ТК», и внешне эти буквы неразличимы. Регистр не важен.
 */
const KIND_LETTERS = '[TtТт][KkКк]';

/**
 * Payload не может содержать «]» — поэтому совпадение не жадное по построению
 * и не растягивается от первой скобки до последней через чужой текст.
 */
const LITERAL_SOURCE = `\\[(${KIND_LETTERS})(?::([^\\]]*))?\\]`;

const FIND_REGEX = new RegExp(LITERAL_SOURCE);
const PARSE_REGEX = new RegExp(`^${LITERAL_SOURCE}$`);

/**
 * Форма для `InputRule.find` — совпадение обязано заканчиваться на конце строки.
 *
 * TipTap вычисляет начало заменяемого диапазона как
 * `from - (match[0].length - text.length)`, то есть отсчитывает его от конца.
 * Незаякоренный регекс дал бы совпадение в середине, и правило съело бы чужой
 * текст слева от метки.
 *
 * Регекс без флага `g`, поэтому `lastIndex` между вызовами не живёт и общий
 * объект безопасен.
 */
export const TK_LITERAL_AT_END_REGEX = new RegExp(`${LITERAL_SOURCE}$`);

const CYRILLIC_KIND_LETTERS: Record<string, string> = { Т: 'T', т: 'T', К: 'K', к: 'K' };

/**
 * Канонизируется ТОЛЬКО kind — невидимый атрибут, по которому считают и фильтруют.
 * Видимый литерал остаётся ровно таким, каким его напечатал автор: переписывать
 * набранные человеком символы ради косметической однородности — цена без выгоды.
 */
function normalizeKind(raw: string): string {
  return Array.from(raw)
    .map((letter) => CYRILLIC_KIND_LETTERS[letter] ?? letter)
    .join('')
    .toUpperCase();
}

/**
 * ВАЛИДАЦИЯ: литералом должна быть вся строка целиком.
 *
 * Единственный источник истины для инварианта целостности — его спрашивают
 * parseHTML при загрузке, appendTransaction при правке и статистика при подсчёте.
 * Без якорей узел с текстом «[TK: a] мусор» считался бы меткой, и «мусор»
 * вместе с ним молча исчезал бы из статистики.
 */
export function parseTkLiteral(text: string): ITkLiteral | null {
  const match = PARSE_REGEX.exec(text);

  if (!match) {
    return null;
  }

  return {
    kind: normalizeKind(match[1]),
    payload: (match[2] ?? '').trim(),
  };
}

/**
 * ПОИСК литерала внутри произвольной строки.
 *
 * Отличается от `parseTkLiteral` намеренно: тот требует, чтобы литералом была
 * вся строка, и держит инвариант целостности, а этот ищет вхождение. Объединять
 * их нельзя — узел с текстом «[TK: a] мусор» прошёл бы поисковую проверку, и
 * «мусор» вместе с меткой молча исчез бы из статистики.
 *
 * Нужен распознаванию литералов во вставленном тексте: там текст уже разобран
 * ProseMirror'ом и декодирован, поэтому искать можно прямо в нём.
 */
export function findTkLiteral(text: string): ITkLiteralMatch | null {
  const match = FIND_REGEX.exec(text);

  if (!match) {
    return null;
  }

  return {
    kind: normalizeKind(match[1]),
    payload: (match[2] ?? '').trim(),
    from: match.index,
    to: match.index + match[0].length,
  };
}

/**
 * Заготовка для команды вставки: именно «[TK: ]», а не «[TK]».
 *
 * У короткой формы первый же введённый символ дал бы «[TKи]» — невалидный
 * литерал, и инвариант целостности развернул бы метку обратно в текст прямо
 * под руками у автора. С двоеточием и пробелом литерал остаётся валидным на
 * каждом промежуточном шаге набора.
 */
export const TK_EMPTY_LITERAL = `[${TK_DEFAULT_KIND}: ]`;

export function buildTkLiteral(kind: string, payload: string): string {
  const trimmed = payload.trim();

  return trimmed ? `[${kind}: ${trimmed}]` : `[${kind}]`;
}
